import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Member, type MemberProfile } from "../api";
import { useAuth } from "../auth";
import { Avatar } from "../components/Avatar";
import { Button, Card, Input, SectionHeading, Tag } from "../components/ui";

const SUGGESTED_GENRES = [
  "literary fiction",
  "historical",
  "thriller",
  "memoir",
  "romance",
  "campus novel",
  "essays",
  "translated",
  "speculative",
  "magical realism",
  "darkly funny",
  "short stories",
  "feminist",
  "queer",
  "atmospheric",
];

export default function ProfilePage() {
  const qc = useQueryClient();
  const { user, refresh, hasPassword } = useAuth();
  const isAdmin = user?.isAdmin === true;
  const me = useQuery({
    queryKey: ["me-profile"],
    queryFn: () => api.get<{ profile: MemberProfile }>("/members/me"),
  });

  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [genres, setGenres] = useState<string[]>([]);
  const [newGenre, setNewGenre] = useState("");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (me.data?.profile) {
      setName(me.data.profile.name ?? "");
      setBio(me.data.profile.bio ?? "");
      setGenres(me.data.profile.favoriteGenres ?? []);
    }
  }, [me.data]);

  const save = useMutation({
    mutationFn: (payload: { name: string | null; bio: string | null; favoriteGenres: string[] }) =>
      api.patch<{ profile: MemberProfile }>("/members/me", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me-profile"] });
      qc.invalidateQueries({ queryKey: ["members"] });
      refresh();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    },
  });

  if (me.isLoading) return null;
  const profile = me.data?.profile;

  function toggleGenre(g: string) {
    setGenres((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));
  }

  return (
    <div className="flex flex-col gap-10">
      <SectionHeading
        eyebrow="About you"
        title="Your profile"
        description="A little context for the rest of the club."
      />

      <Card accent>
        <div className="flex flex-col md:flex-row gap-8">
          <div className="flex flex-col items-center gap-3 shrink-0">
            <Avatar
              name={name || profile?.email}
              email={profile?.email}
              hue={profile?.avatarHue}
              size="xl"
              className="w-28 h-28 text-4xl"
            />
            <p className="text-xs text-plum-mute text-center">
              Avatar is generated<br />from your email
            </p>
          </div>

          <div className="flex-1 flex flex-col gap-5">
            <Input
              label="Display name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="What should we call you?"
            />

            <div>
              <label className="text-xs font-medium uppercase tracking-[0.12em] text-plum-soft block mb-1.5">
                A line about you
              </label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={3}
                placeholder="A few words on what kinds of stories you love…"
                className="w-full px-4 py-3 rounded-xl bg-cream-50 border border-cream-300 text-plum placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose resize-none"
              />
            </div>

            <div>
              <label className="text-xs font-medium uppercase tracking-[0.12em] text-plum-soft block mb-2.5">
                Favorite genres
              </label>
              {genres.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-3">
                  {genres.map((g) => (
                    <button
                      key={g}
                      onClick={() => toggleGenre(g)}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose text-white text-xs font-medium hover:bg-rose-dark"
                    >
                      {g} <span>×</span>
                    </button>
                  ))}
                </div>
              ) : null}

              <div className="flex flex-wrap gap-2">
                {SUGGESTED_GENRES.filter((g) => !genres.includes(g)).map((g) => (
                  <button
                    key={g}
                    onClick={() => toggleGenre(g)}
                    className="inline-flex items-center px-3 py-1 rounded-full bg-cream-100 text-plum-soft text-xs hover:bg-blush-soft hover:text-rose-dark"
                  >
                    + {g}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 mt-3">
                <input
                  type="text"
                  value={newGenre}
                  onChange={(e) => setNewGenre(e.target.value)}
                  placeholder="Add a custom genre"
                  className="flex-1 px-3 py-2 rounded-lg bg-cream-50 border border-cream-300 text-sm text-plum placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newGenre.trim()) {
                      e.preventDefault();
                      if (!genres.includes(newGenre.trim())) {
                        setGenres((g) => [...g, newGenre.trim()]);
                      }
                      setNewGenre("");
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              {savedAt ? (
                <Tag>Saved ✓</Tag>
              ) : null}
              <Button
                onClick={() =>
                  save.mutate({
                    name: name.trim() || null,
                    bio: bio.trim() || null,
                    favoriteGenres: genres,
                  })
                }
                loading={save.isPending}
              >
                Save profile
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <PasswordCard hasPassword={hasPassword} onChanged={refresh} />

      {isAdmin ? <AdminMembersCard meId={user!.id} /> : null}
    </div>
  );
}

function AdminMembersCard({ meId }: { meId: number }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [grantAdmin, setGrantAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");

  const members = useQuery({
    queryKey: ["admin-members"],
    queryFn: () => api.get<{ members: Member[] }>("/admin/members"),
  });

  const fullName = () => [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");

  const invite = useMutation({
    mutationFn: () => {
      const name = fullName();
      return api.post<{ member: Member; invited: boolean }>("/admin/members", {
        email: email.trim(),
        isAdmin: grantAdmin,
        ...(name ? { name } : {}),
      });
    },
    onSuccess: (res) => {
      setEmail("");
      setFirstName("");
      setLastName("");
      setGrantAdmin(false);
      setError(null);
      setNotice(
        res.invited
          ? `Invite email sent to ${res.member.email}.`
          : `${res.member.email} was added, but the invite email could not be sent (check email config).`
      );
      qc.invalidateQueries({ queryKey: ["admin-members"] });
    },
    onError: (e: Error) => {
      setNotice(null);
      setError(e.message);
    },
  });

  const resend = useMutation({
    mutationFn: (id: number) =>
      api.post<{ invited: boolean }>(`/admin/members/${id}/invite`, {}),
    onSuccess: (res, id) => {
      const m = members.data?.members.find((x) => x.id === id);
      setError(null);
      setNotice(
        res.invited
          ? `Invite email re-sent to ${m?.email ?? "member"}.`
          : `Could not send the invite email (check email config).`
      );
    },
    onError: (e: Error) => {
      setNotice(null);
      setError(e.message);
    },
  });

  const toggleAdmin = useMutation({
    mutationFn: ({ id, isAdmin }: { id: number; isAdmin: boolean }) =>
      api.patch(`/admin/members/${id}`, { isAdmin }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-members"] }),
    onError: (e: Error) => setError(e.message),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string | null }) =>
      api.patch(`/admin/members/${id}`, { name }),
    onSuccess: () => {
      setEditingId(null);
      setEditName("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["admin-members"] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/members/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-members"] }),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Card accent>
      <SectionHeading
        eyebrow="Admin only"
        title="Members & access"
        description="Add a member's name and email so they can sign in. An invite email with a sign-in link goes out automatically. Remove an email to revoke access."
      />

      <div className="mt-6 flex flex-col gap-3 max-w-lg">
        <div className="flex gap-3">
          <Input
            label="First name"
            placeholder="Jane"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setError(null);
            }}
          />
          <Input
            label="Last name"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setError(null);
            }}
          />
        </div>
        <Input
          label="Email"
          type="email"
          placeholder="new-member@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          error={error}
        />
        <label className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-ink-mute">
          <input
            type="checkbox"
            checked={grantAdmin}
            onChange={(e) => setGrantAdmin(e.target.checked)}
            className="accent-bordeaux"
          />
          Grant admin role
        </label>
        <div>
          <Button
            onClick={() => invite.mutate()}
            loading={invite.isPending}
            disabled={!email.trim()}
          >
            Add member & send invite
          </Button>
        </div>
        {notice ? (
          <p className="font-mono text-[11px] tracking-[0.06em] text-ink-mute">{notice}</p>
        ) : null}
      </div>

      <div className="mt-8 border-t border-rule">
        {members.data?.members.map((m) => (
          <div
            key={m.id}
            className="flex items-center justify-between gap-3 py-3 border-b border-rule"
          >
            <div className="flex flex-col flex-1 min-w-0">
              {editingId === m.id ? (
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Full name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    autoFocus
                  />
                  <button
                    onClick={() => rename.mutate({ id: m.id, name: editName.trim() || null })}
                    disabled={rename.isPending}
                    className="font-mono text-[10px] uppercase tracking-[0.22em] text-bordeaux hover:text-bordeaux-deep px-2 py-1"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingId(null);
                      setEditName("");
                    }}
                    className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-mute hover:text-ink px-2 py-1"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <span className="text-ink">{m.name ?? m.email.split("@")[0]}</span>
                  <span className="font-mono text-[11px] tracking-[0.06em] text-ink-mute truncate">
                    {m.email}
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {m.isAdmin ? <Tag>Admin</Tag> : null}
              {editingId !== m.id ? (
                <button
                  onClick={() => {
                    setEditingId(m.id);
                    setEditName(m.name ?? "");
                  }}
                  className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-mute hover:text-bordeaux px-2 py-1"
                >
                  {m.name ? "Edit name" : "Add name"}
                </button>
              ) : null}
              <button
                onClick={() => resend.mutate(m.id)}
                disabled={resend.isPending}
                className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-mute hover:text-bordeaux px-2 py-1"
              >
                Resend invite
              </button>
              {m.id !== meId ? (
                <>
                  <button
                    onClick={() =>
                      toggleAdmin.mutate({ id: m.id, isAdmin: !m.isAdmin })
                    }
                    className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-mute hover:text-bordeaux px-2 py-1"
                  >
                    {m.isAdmin ? "Demote" : "Make admin"}
                  </button>
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          `Remove ${m.email}? They'll lose access immediately and their picks/notes stay attributed to them.`
                        )
                      ) {
                        remove.mutate(m.id);
                      }
                    }}
                    className="font-mono text-[10px] uppercase tracking-[0.22em] text-hot-rose hover:text-bordeaux-deep px-2 py-1"
                  >
                    Remove
                  </button>
                </>
              ) : (
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-mute">
                  You
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PasswordCard({ hasPassword, onChanged }: { hasPassword: boolean; onChanged: () => void }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (password.length < 8) {
        throw new Error("Password must be at least 8 characters.");
      }
      if (password !== confirm) {
        throw new Error("Passwords don't match.");
      }
      await api.post("/auth/password", { password });
    },
    onSuccess: () => {
      setPassword("");
      setConfirm("");
      setError(null);
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
      onChanged();
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.del("/auth/password"),
    onSuccess: () => onChanged(),
  });

  return (
    <Card accent>
      <SectionHeading
        eyebrow={hasPassword ? "Sign-in" : "First-time setup"}
        title={hasPassword ? "Change your password" : "Set a password"}
        description={
          hasPassword
            ? "Update the password you use to sign in. Existing sessions stay signed in."
            : "Set a password so you can sign in directly next time — no magic link needed."
        }
      />

      <div className="mt-5 flex flex-col gap-4 max-w-md">
        <Input
          label={hasPassword ? "New password" : "Password"}
          type="password"
          autoComplete="new-password"
          placeholder="At least 8 characters"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError(null);
          }}
        />
        <Input
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          placeholder="Type it again"
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            setError(null);
          }}
          error={error}
        />
        <div className="flex items-center justify-between gap-3 pt-1">
          {hasPassword ? (
            <button
              type="button"
              onClick={() => {
                if (confirm.length > 0 || password.length > 0) return;
                if (window.confirm("Remove your password? You'll need a magic link to sign in next time.")) {
                  remove.mutate();
                }
              }}
              className="text-xs uppercase tracking-[0.14em] text-plum-mute hover:text-rose disabled:opacity-50"
              disabled={password.length > 0 || confirm.length > 0 || remove.isPending}
              title={password.length > 0 || confirm.length > 0 ? "Clear the fields above to remove your password" : ""}
            >
              {remove.isPending ? "Removing…" : "Remove password"}
            </button>
          ) : <span />}
          <div className="flex items-center gap-3">
            {savedAt ? <Tag>Saved ✓</Tag> : null}
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={password.length === 0}
            >
              {hasPassword ? "Update password" : "Set password"}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
